import { getCookies, setCookies, removeAllCookies } from "../utils";
import { applyUILanguageToI18nIfNeeded } from "../utils/personalSettingLanguage";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { message } from "antd";
import i18n from "../i18n";
import {
  isPushSupported,
  getCurrentSubscription,
  savePushSubscription,
  unbindDeviceForLogout,
  subscribeUserToPush,
  wasBound,
  setWasBound,
  PUSH_STATUS_CHANGED,
} from "../services/pushService";

/** 登入後導向目標：路徑（存於 sessionStorage） */
export const LOGIN_REDIRECT_PATH_KEY = "login_redirect_path";
/** 登入後導向目標：查詢字串（存於 sessionStorage） */
export const LOGIN_REDIRECT_SEARCH_KEY = "login_redirect_search";
export const LOGIN_RESPONSE_KEY = "loginResponse";

const COOKIE_MAX_AGE = 86400;

/**
 * 經銷商切換客戶成功後：更新 cookie、與登入相同之 session 快取，並整頁導向主選單（讓權限與語系一次載入）
 */
export function applyClientSwitchSuccess(results) {
  setLoginCookies(results);
  sessionStorage.setItem(LOGIN_RESPONSE_KEY, JSON.stringify(results, null, 2));
  const base = import.meta.env.BASE_URL || "/";
  const root = base.endsWith("/") ? base : `${base}/`;
  const { pathname, search } = new URL(
    "menu",
    `${window.location.origin}${root}`
  );
  window.location.replace(pathname + search);
}

/** 登入成功後寫入 cookie（auth 相關） */
export function setLoginCookies(results) {
  setCookies("authToken", results.token, { maxAge: COOKIE_MAX_AGE });
  setCookies("client_id", results.client_id, { maxAge: COOKIE_MAX_AGE });
  setCookies("client_prefix", results.client_prefix, { maxAge: COOKIE_MAX_AGE });
  setCookies("web_setting", results.web_setting, { maxAge: COOKIE_MAX_AGE });
  setCookies("user_name", results.user_name, { maxAge: COOKIE_MAX_AGE });
  setCookies("user_id", results.user_id, { maxAge: COOKIE_MAX_AGE });
  setCookies("is_admin", results.is_admin, { maxAge: COOKIE_MAX_AGE });
  setCookies("permission", JSON.stringify(results.web_feature_permission), {
    maxAge: COOKIE_MAX_AGE,
  });
  setCookies("personal_setting", results.personal_setting, {
    maxAge: COOKIE_MAX_AGE,
  });
  setCookies("region", results.client_region, { maxAge: COOKIE_MAX_AGE });
  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  setCookies("LocalTimeZone", localTimeZone, { maxAge: COOKIE_MAX_AGE });
}

/** 登入成功後：寫入 cookie、sessionStorage，並導向原本目標或 /test */
export function onLoginSuccess(results, navigate) {
  setLoginCookies(results);
  applyUILanguageToI18nIfNeeded(i18n);
  sessionStorage.setItem(LOGIN_RESPONSE_KEY, JSON.stringify(results, null, 2));

  const redirectPath = sessionStorage.getItem(LOGIN_REDIRECT_PATH_KEY);
  const redirectSearch =
    sessionStorage.getItem(LOGIN_REDIRECT_SEARCH_KEY) || "";
  if (redirectPath && redirectPath !== "/" && redirectPath !== "/login") {
    sessionStorage.removeItem(LOGIN_REDIRECT_PATH_KEY);
    sessionStorage.removeItem(LOGIN_REDIRECT_SEARCH_KEY);
    navigate(redirectPath + redirectSearch, { replace: true });
  } else {
    navigate("/menu", { replace: true });
  }
}

/** 清除與登入相關的 sessionStorage（含登入回傳資料、導向目標） */
export function clearLoginStorage() {
  sessionStorage.removeItem(LOGIN_RESPONSE_KEY);
  sessionStorage.removeItem(LOGIN_REDIRECT_PATH_KEY);
  sessionStorage.removeItem(LOGIN_REDIRECT_SEARCH_KEY);
}

/** 是否為 admin（登入回傳 is_admin 為 true / false；寫入 cookie 後讀取多為 boolean，少數環境可能為字串 "true"） */
export function isAdminFromCookie() {
  const v = getCookies("is_admin");
  return v === true || v === "true";
}

/**
 * 這個 session 是否已經同步過推播綁定。
 *
 * 必須是 module 層變數而非 `useRef`：每個路由物件各自包一層 `<AuthedShell>`
 * （見 `src/router/index.jsx`），切換路由時 `Auth` 會 remount，`useRef` 會被重置，
 * 變成每次換頁都打一次 API。
 */
let hasSyncedPushSubscription = false;

/**
 * 登入（含切換客戶整頁重載、同裝置換帳號）後靜默補一次綁定，一個 session 只做一次。
 *
 * 只在「使用者本來就綁定過」時才做 —— 權限為 granted 且本機訂閱存在。
 * 沒綁定過的人不該自動幫他綁。
 *
 * 附帶效果：後端門牌若曾被 404/410 清掉，會在這裡自動補回，
 * 「本機有訂閱但後端沒紀錄」的假已綁定狀態不必另外偵測（設計文件 §4.1）。
 */
async function syncPushSubscriptionOnce() {
  if (hasSyncedPushSubscription) return;
  // 先設旗標再跑，避免 remount 或並行 render 重複打 API
  hasSyncedPushSubscription = true;
  try {
    if (!isPushSupported() || Notification.permission !== "granted") return;
    let sub = await getCurrentSubscription();
    let recovered = false;
    // 權限還在但訂閱沒了（push service 讓門牌失效、後端收 410 已刪）：
    // 開過且沒自己關掉的人，靜默重訂。權限已 granted 時 Chrome 不需使用者手勢；
    // 需要手勢的平台（iOS）會 throw，落回首頁引導卡讓使用者按一下。
    if (!sub && wasBound()) {
      sub = await subscribeUserToPush();
      recovered = true;
    }
    if (!sub) return;
    await savePushSubscription(sub);
    // 舊版綁定的使用者沒有標記，同步成功時順便補上
    setWasBound(true);
    if (recovered) {
      // 首頁引導卡在重訂前已推導成 unbound，通知它重算
      window.dispatchEvent(new Event(PUSH_STATUS_CHANGED));
      message.success(i18n.t("home.pushGuide.recovered"));
    }
  } catch (e) {
    console.error("自動重綁失敗，略過", e);
  }
}

/**
 * 登出：解除這台裝置的推播綁定，再清除 cookie 與 sessionStorage，並導向登入頁。
 *
 * ⚠️ **順序不可反**：`removeAllCookies()` 一執行，axios 攔截器就讀不到 `authToken`，
 * DELETE 會缺 Authorization；也不能 fire-and-forget（同樣來不及）。
 *
 * 解綁只打後端、不 `unsubscribe()`，所以重新登入時會自動恢復收訊（設計文件 §3）。
 */
export async function logout(navigate) {
  await unbindDeviceForLogout();
  // 同一個 tab 內登出再登入也要能重綁，旗標必須重設
  hasSyncedPushSubscription = false;
  removeAllCookies();
  clearLoginStorage();
  navigate("/login", { replace: true });
}

export default function Auth({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const token = getCookies("authToken");
  const permission = getCookies("permission");

  useEffect(() => {
    if (!token) {
      sessionStorage.setItem(LOGIN_REDIRECT_PATH_KEY, location.pathname);
      sessionStorage.setItem(
        LOGIN_REDIRECT_SEARCH_KEY,
        location.search ? location.search : ""
      );
      navigate("/login", { replace: true });
      return;
    }
    if (!permission) {
      removeAllCookies();
      clearLoginStorage();
      navigate("/login", { replace: true });
    }
  }, [token, permission, location.pathname, location.search, navigate]);

  // 已登入才同步推播綁定：這裡是「已登入」的唯一守門，一般登入、切換客戶整頁重載、
  // 同裝置換帳號全部會經過（掛在 onLoginSuccess 涵蓋不到切換客戶）
  useEffect(() => {
    if (token && permission) syncPushSubscriptionOnce();
  }, [token, permission]);

  if (!token || !permission) {
    return null;
  }

  return <>{children}</>;
}
